import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {ModalType, RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {IModalMessage, TModalArguments} from "data/types/modals";
import {SyntheticEvent} from "react";

interface IScoreTabsData {
	position: number;
	tabName: string;
	tabData: ITabData[];
}

interface ITabData {
	name: string;
	value: string;
}

export interface IHowToScoreModalController extends ViewController {
	readonly i18n: ILocalizationStore;
	get visibleModal(): ModalType | null;
	get modalContent(): IModalMessage | null;
	get tabsDataArr(): IScoreTabsData[];
	get currentTab(): string;

	close: () => void;
	show: (...args: TModalArguments) => void;
	onChangeTab: (_event: SyntheticEvent, newValue: string) => void;
}

@injectable()
export class HowToScoreModalController implements IHowToScoreModalController {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable _currentTab: string = this.i18n.t(
		"modal.how_to_score.tabs.hole_by_hole",
		"hole-by-hole"
	);
	@observable private _tabsDataArr: IScoreTabsData[] = [
		{
			position: 1,
			tabName: this.i18n.t("modal.how_to_score.tabs.hole_by_hole", "hole-by-hole"),
			tabData: [
				{
					name: this.i18n.t("modal.how_to_score.cell.win_hole", "Win Hole (Pts)"),
					value: "+3",
				},
				{
					name: this.i18n.t("modal.how_to_score.cell.halve_hole", "Halve Hole (Pts)"),
					value: "+1",
				},
				{
					name: this.i18n.t("modal.how_to_score.cell.lose_hole", "Lose Hole (Pts)"),
					value: "-1",
				},
			],
		},
		{
			position: 2,
			tabName: this.i18n.t("modal.how_to_score.tabs.shot_by_shot", "shot-by-shot"),
			tabData: [
				{
					name: this.i18n.t("modal.how_to_score.cell.fairway_hit", "Fairway Hit (Pts)"),
					value: "+0.5",
				},
				{
					name: this.i18n.t(
						"modal.how_to_score.cell.green_regulation",
						"Green In Regulation (Pts)"
					),
					value: "+0.5",
				},
				{
					name: this.i18n.t("modal.how_to_score.cell.hole_in_one", "Hole-in-one (Pts)"),
					value: "+5",
				},
			],
		},
		{
			position: 3,
			tabName: this.i18n.t("modal.how_to_score.tabs.bonus", "bonus"),
			tabData: [
				{
					name: this.i18n.t(
						"modal.how_to_score.cell.holes_won_in_row",
						"3 Holes Won In A Row (Pts)"
					),
					value: "+5",
				},
				{
					name: this.i18n.t(
						"modal.how_to_score.cell.holes_lost_in_match",
						"No Holes Lost In Match (Pts)"
					),
					value: "+5",
				},
				{
					name: this.i18n.t("modal.how_to_score.cell.win_match", "Win Match (Pts)"),
					value: "+6",
				},
				{
					name: this.i18n.t("modal.how_to_score.cell.tie_match", "Tie Match (Pts)"),
					value: "+2",
				},
				{
					name: this.i18n.t(
						"modal.how_to_score.cell.unplayed_holes",
						"Unplayed Holes Win Only (Pts)"
					),
					value: "+1 Per hole",
				},
			],
		},
	];

	get currentTab() {
		return this._currentTab;
	}

	constructor(
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore
	) {
		makeAutoObservable(this);
	}

	get visibleModal() {
		return this._modalsStore.modal;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	get tabsDataArr() {
		return this._tabsDataArr;
	}

	show = (...args: TModalArguments) => {
		this._modalsStore.showModal(...args);
	};

	close = () => {
		// this._roundStore.setSelectedSessionId(this._roundStore.selectedRoundSessions[1].id ?? 0);
		this._modalsStore.hideModal();
	};

	onChangeTab = (_event: SyntheticEvent, newValue: string) => {
		this._currentTab = newValue;
	};

	dispose() {
		return;
	}

	init(param: void) {
		return;
	}
}
